import React, { useState, forwardRef, useEffect, useRef } from 'react';
import TextField from '@mui/material/TextField';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const CaseNotesInput = forwardRef(({ initialValue, onValueChange }, ref) => {
  const [localValue, setLocalValue] = useState(initialValue || '');
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const textareaRef = useRef(null);

  // Update local state when initialValue changes
  useEffect(() => {
    setLocalValue(initialValue || '');
  }, [initialValue]);

  // Check if content is scrollable and handle scroll position
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      const isScrollable = textarea.scrollHeight > textarea.clientHeight;
      setShowScrollIndicator(isScrollable);

      const handleScroll = () => {
        const isBottom = Math.abs(textarea.scrollHeight - textarea.scrollTop - textarea.clientHeight) < 1;
        setIsAtBottom(isBottom);
      };

      textarea.addEventListener('scroll', handleScroll);
      return () => textarea.removeEventListener('scroll', handleScroll);
    }
  }, [localValue]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onValueChange?.(newValue);
  };

  return (
    <div className="flex flex-grow relative">
      <TextField
        hiddenLabel
        className="flex-grow"
        id="filled-hidden-label-normal"
        placeholder="Type any internal case notes here"
        autoComplete="off"
        value={localValue}
        onChange={handleChange}
        variant="outlined"
        multiline
        minRows={2}
        maxRows={2}
        size="small"
        inputRef={textareaRef}
        InputProps={{
          sx: {
            padding: 0,
            '& .MuiInputBase-inputMultiline': {
              padding: '6px 8px',
              boxSizing: 'content-box',
              overflow: 'auto',
              fontSize: '0.875rem',
              lineHeight: 1.5,
            },
          },
        }}
      />
      {showScrollIndicator && (
        <div className={`absolute ${isAtBottom ? 'top-0' : 'bottom-0'} left-1/2 transform -translate-x-1/2 pointer-events-none`}>
          {isAtBottom ? (
            <KeyboardArrowUpIcon className="text-gray-400 animate-bounce" />
          ) : (
            <KeyboardArrowDownIcon className="text-gray-400 animate-bounce" />
          )}
        </div>
      )}
    </div>
  );
});

export default React.memo(CaseNotesInput);
